import { createFetchSender, createSuperagentSender, isNode } from './utils';
import type { LogEntry, SendLogsFn, TransportConfig } from './types';

/**
 * Better Stack logger with buffering and auto-flush.
 * Buffers logs and sends in batches (max 50 logs or every 2s) to reduce HTTP overhead.
 * Auto-selects transport: superagent in Node.js (with proxy support), fetch in browser.
 */
export class BetterStackLogger {
  private buffer: LogEntry[] = [];
  private readonly flushInterval: ReturnType<typeof setInterval>;
  private readonly sendLogs: SendLogsFn;
  private readonly maxBufferSize: number;
  private readonly flushIntervalMs: number;

  constructor(options: TransportConfig) {
    if (options.sendLogs) {
      this.sendLogs = options.sendLogs;
    } else if (isNode()) {
      this.sendLogs = createSuperagentSender(options.ingestUrl, options.token, options.getProxy);
    } else {
      this.sendLogs = createFetchSender(options.ingestUrl, options.token);
    }
    this.maxBufferSize = options.maxBufferSize ?? 50;
    this.flushIntervalMs = options.flushIntervalMs ?? 2000;
    this.flushInterval = setInterval(() => this.flush(), this.flushIntervalMs);
  }

  addLog(entry: LogEntry): void {
    this.buffer.push(entry);
    if (this.buffer.length >= this.maxBufferSize) {
      this.flush();
    }
  }

  async flush(): Promise<void> {
    if (this.buffer.length === 0) return;

    const logs = [...this.buffer];
    this.buffer = [];

    try {
      await this.sendLogs(logs);
    } catch {
      // Never throw from a logger - silently drop on failure
    }
  }

  async waitForFlush(): Promise<void> {
    clearInterval(this.flushInterval);
    await this.flush();
  }

  destroy(): void {
    clearInterval(this.flushInterval);
  }
}
